import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { ALLOCATION_METHOD, BILL_FREQUENCY } from 'src/constants/utilities';
import { apPushNeeds, dayLabel, sourceNeeds } from './setupSummary';

/** The longest name property-service takes for a setup. */
export const MAX_SETUP_NAME_LENGTH = 200;

/** The bill sources a setup can pick, in the PRD mockup's order and words (FR-6). */
export const SOURCE_CHOICES = [{
  value: 'Email',
  title: 'Email',
  sub: 'Forward bills to your intake address'
}, {
  value: 'UtilityPortal',
  title: 'Utility portal',
  sub: 'Livable signs in each month'
}, {
  value: 'Upload',
  title: 'Upload to Livable Pro',
  sub: 'My team uploads the files into Livable Pro'
}, {
  value: 'NotSent',
  title: 'No bills sent to Livable',
  sub: 'Bills are typed in, or not billed'
}];

/** Shown, never chosen: bills by mail are coming soon. */
export const MAIL_CHOICE = {
  title: 'Mail to Livable',
  sub: 'Paper bills to a Livable address'
};
export const FREQUENCY_CHOICES = [BILL_FREQUENCY.Monthly, BILL_FREQUENCY.BiMonthly, BILL_FREQUENCY.Trimonthly, BILL_FREQUENCY.Annually];

// The RUBS methods utility setup offers a utility today (AllocationMethodModal), in its order.
const RUBS_METHODS = [ALLOCATION_METHOD.ByOccupancy, ALLOCATION_METHOD.BySquareFootage, ALLOCATION_METHOD.ByUnit, ALLOCATION_METHOD.ByPercentageShare, ALLOCATION_METHOD.By50Occupancy50SquareFootage];

/** Methods that split by each property's square footage, which the property has to have. */
export const SQUARE_FOOTAGE_METHODS = [ALLOCATION_METHOD.BySquareFootage, ALLOCATION_METHOD.By50Occupancy50SquareFootage];
const VALUE_KEYS = ['billSource', 'billToResidents', 'allocationMethod', 'billFrequencyType', 'isApPushEnabled'];

/** The five values an edit sends, from a setup or its pending edit. */
export const valuesOf = values => {
  var _values$billSource;
  return {
    billSource: (_values$billSource = values.billSource) !== null && _values$billSource !== void 0 ? _values$billSource : null,
    billToResidents: !!values.billToResidents,
    allocationMethod: values.allocationMethod,
    billFrequencyType: values.billFrequencyType,
    isApPushEnabled: !!values.isApPushEnabled
  };
};

/**
 * Where the editor starts: the setup's name, and the values its pending edit applies when it has one. Those are what
 * the setup will hold, and saving replaces the pending edit, so starting from the current values would quietly drop
 * the rest of it.
 */
export const draftOf = setup => {
  var _setup$pendingEdit;
  return _objectSpread({
    name: setup.name
  }, valuesOf((_setup$pendingEdit = setup.pendingEdit) !== null && _setup$pendingEdit !== void 0 ? _setup$pendingEdit : setup));
};
export const sameValues = (a, b) => VALUE_KEYS.every(key => {
  var _a$key, _b$key;
  return ((_a$key = a === null || a === void 0 ? void 0 : a[key]) !== null && _a$key !== void 0 ? _a$key : null) === ((_b$key = b === null || b === void 0 ? void 0 : b[key]) !== null && _b$key !== void 0 ? _b$key : null);
});

/**
 * The preview's answer for the draft's values, keyed by them: one that answered for other values reads as still being
 * asked. A changed value re-renders before the new preview starts, and the last answer mustn't speak for it meanwhile.
 */
export const previewFor = (preview, valuesKey) => preview.key === valuesKey ? preview : {
  key: valuesKey,
  state: 'loading',
  result: null,
  message: ''
};

/**
 * A bill source the company's plan doesn't include, with the plan it needs. It's locked unless the setup already holds
 * it: property-service checks a value only when it changes, so a setup keeps what it has. Nothing is locked while the
 * plan isn't known.
 */
export const sourceChoice = (source, setup, allows) => {
  const needs = sourceNeeds(source, allows);
  return {
    needs,
    locked: !!needs && setup.billSource !== source
  };
};

/**
 * AP Push is an add-on for Plus and Autopilot (FR-28): on Pro it can't be turned on, unless the setup already has it.
 * Nor can it while the plan isn't known: the server doesn't refuse it on Pro yet (LB-6188), so a switch turned on before
 * the plan arrived would save it.
 */
export const apPushChoice = (setup, allows) => {
  const needs = apPushNeeds(allows);
  const locked = !setup.isApPushEnabled && (!allows || !!needs);
  // Only worth saying when it's what holds the switch.
  return {
    needs,
    locked,
    planUnknown: locked && !allows
  };
};
export const isSubmeterSetup = setup => setup.allocationMethod === ALLOCATION_METHOD.BySubmeter;

/**
 * The methods a setup can pick. A Sub-meter setup stays Sub-meter, and a RUBS setup picks among the RUBS methods: every
 * utility would need its own meters before it could move between them. "By number of occupants" needs the company's
 * opt-in, unless the setup already uses it.
 */
export const methodChoices = (setup, occupantsOptIn) => {
  var _setup$pendingEdit2;
  if (isSubmeterSetup(setup)) return [ALLOCATION_METHOD.BySubmeter];
  const held = [setup.allocationMethod, (_setup$pendingEdit2 = setup.pendingEdit) === null || _setup$pendingEdit2 === void 0 ? void 0 : _setup$pendingEdit2.allocationMethod].filter(Boolean);
  const methods = [...RUBS_METHODS];
  if (occupantsOptIn || held.includes(ALLOCATION_METHOD.ByNumberOfOccupants)) {
    methods.push(ALLOCATION_METHOD.ByNumberOfOccupants);
  }
  // A method this list doesn't name stays a choice while the setup holds it.
  held.forEach(method => {
    if (!methods.includes(method)) methods.push(method);
  });
  return methods;
};

/**
 * The methods a new setup can pick (LB-6193): the RUBS methods, and Sub-meter, which no edit can reach. "By number of
 * occupants" needs the company's opt-in.
 */
export const newSetupMethods = occupantsOptIn => [...RUBS_METHODS, ...(occupantsOptIn ? [ALLOCATION_METHOD.ByNumberOfOccupants] : []), ALLOCATION_METHOD.BySubmeter];

/**
 * Where New setup starts: the values of the setup it's modelled on, usually the type's most-used, so a new setup differs
 * only where it's changed. A method the new setup can't pick falls back to Occupancy. Bill to residents is always on
 * until Phase 2.
 */
export const newSetupDraftFrom = (setup, methods) => {
  var _setup$billSource;
  return {
    billSource: (_setup$billSource = setup === null || setup === void 0 ? void 0 : setup.billSource) !== null && _setup$billSource !== void 0 ? _setup$billSource : null,
    billToResidents: true,
    allocationMethod: methods.includes(setup === null || setup === void 0 ? void 0 : setup.allocationMethod) ? setup.allocationMethod : ALLOCATION_METHOD.ByOccupancy,
    billFrequencyType: FREQUENCY_CHOICES.includes(setup === null || setup === void 0 ? void 0 : setup.billFrequencyType) ? setup.billFrequencyType : BILL_FREQUENCY.Monthly,
    isApPushEnabled: !!(setup !== null && setup !== void 0 && setup.isApPushEnabled)
  };
};
const count = (n, one, many) => "".concat(n, " ").concat(n === 1 ? one : many);

/**
 * The line above Save (FR-3), and whether Save is open. `baseline` is where the editor started and `result` the preview's
 * answer for the draft's values: null while it's being asked (`counting`), or when it was refused or failed, which the
 * editor says itself, so the line is then empty.
 */
export const impactLine = _ref => {
  let setup = _ref.setup,
    baseline = _ref.baseline,
    draft = _ref.draft,
    result = _ref.result,
    _ref$counting = _ref.counting,
    counting = _ref$counting === void 0 ? true : _ref$counting;
  const renamed = draft.name.trim() !== setup.name.trim();
  if (sameValues(draft, baseline)) {
    if (!renamed) return {
      text: 'Change something to save.',
      canSave: false
    };
    // A rename of a setup with a pending edit sends that edit's values again, and property-service compares them
    // with the setup's own: they wait for their month as before, or apply now when no follower has been billed since.
    if (!setup.pendingEdit || sameValues(draft, setup)) {
      return {
        text: 'Renames the setup. Nothing it bills changes.',
        canSave: true
      };
    }
    if (result !== null && result !== void 0 && result.effectiveFrom) {
      return {
        text: "Renames the setup. The scheduled edit still applies from ".concat(dayLabel(result.effectiveFrom), "."),
        canSave: true
      };
    }
  }
  if (setup.pendingEdit && sameValues(draft, setup)) {
    return {
      text: "Saving withdraws the edit scheduled from ".concat(dayLabel(setup.pendingEdit.effectiveFrom), "."),
      canSave: true
    };
  }
  if (!result) return {
    text: counting ? 'Counting what this changes…' : '',
    canSave: false
  };
  const _result$impact = result.impact,
    utilities = _result$impact.utilities,
    properties = _result$impact.properties;
  const reaches = "".concat(count(utilities, 'utility', 'utilities'), " at ").concat(count(properties, 'property', 'properties'));
  if (result.effectiveFrom) {
    return {
      text: "Saving changes ".concat(reaches, ", from ").concat(dayLabel(result.effectiveFrom), ". Months already closed keep their settings."),
      canSave: true
    };
  }
  if (utilities === 0) return {
    text: 'No utility follows this setup yet, so saving changes it now.',
    canSave: true
  };
  return {
    text: "Saving changes ".concat(reaches, " now: none of them has been billed yet."),
    canSave: true
  };
};

/** What the Setup section says once an edit is saved; the setups list names the setup. */
export const savedLine = function (result, appliedNow) {
  var _result$impact2;
  let name = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
  const saved = name ? "Saved ".concat(name, ".") : 'Saved.';
  if (result !== null && result !== void 0 && result.effectiveFrom) return "".concat(saved, " The new settings apply from ").concat(dayLabel(result.effectiveFrom), ".");
  if (appliedNow && result !== null && result !== void 0 && (_result$impact2 = result.impact) !== null && _result$impact2 !== void 0 && _result$impact2.utilities) {
    const utilities = result.impact.utilities;
    return "".concat(saved, " ").concat(count(utilities, 'utility uses', 'utilities use'), " the new settings now.");
  }
  return saved;
};